import React, { Component } from 'react';
import Typography from '@material-ui/core/Typography';
import { CommandIcon } from '../components/CommandIcon';
import Grid from '@material-ui/core/Grid';
import Tooltip from '@material-ui/core/Tooltip';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import IndeterminateCheckBoxIcon from '@material-ui/icons/IndeterminateCheckBox';
import OnlineIcon from '../components/OnlineIcon';
import PauseIcon from '@material-ui/icons/PauseCircleFilled';
import InfoIcon from '@material-ui/icons/Info';
import FilterIcon from '@material-ui/icons/FilterList';
import FirstIcon from '@material-ui/icons/LooksOne';
import RefreshIcon from '@material-ui/icons/Refresh';
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward';
import { AddNewButton } from './AddNewButton';
import IconButton from '@material-ui/core/IconButton';
import MaterialTable, { MTableToolbar } from 'material-table';
import { Link, withRouter } from 'react-router-dom';
import { DEVICES, DEVICE_GROUPS, createInfoUrl } from '../utils/routes';
import InputLabel from '@material-ui/core/InputLabel';
import FormControl from '@material-ui/core/FormControl';
import Select from '@material-ui/core/Select';
import { withStyles } from '@material-ui/core/styles';
import { connect } from 'react-redux';
import { getInstalledAppVersion } from '../redux/actions/deviceActions';
import _ from 'lodash';
import { timeStampToLongString } from '../utils/stringUtils';
import store from 'store';
import compareVersions from 'compare-versions';
import { ShowHide } from '../components/ShowHide';
import { isCurrentUserSuperAdmin } from '../utils/userRoles';
import Box from '@material-ui/core/Box';
import Paper from '@material-ui/core/Paper';
import Collapse from '@material-ui/core/Collapse';
import {
    getDeviceGroupName,
    getDevicePUIVersion,
    getDeviceLastCheckInString,
    getDeviceOrganizationName,
    getDevicePicoControllerVersion,
} from '../utils/deviceUtils';
import { bytesToGb } from '../utils/deviceDialogUtils';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import WarningIcon from '@material-ui/icons/Warning';

const styles = (theme) => ({
    pageTitle: {
        fontWeight: 'bold',
        fontSize: '1.6em',
        marginBottom: theme.spacing(2),
    },
    formControl: {
        marginRight: theme.spacing(2),
        minWidth: '200px',
    },
    barContainer: {
        height: '200px',
    },
    searchContainer: {
        display: 'flex',
        justifyContent: 'flex-end',
    },
    success: {
        color: theme.palette.primary.success,
    },
    error: {
        color: theme.palette.primary.error,
    },
    warning: {
        color: theme.palette.primary.warning,
    },
    infoIcon: {
        padding: theme.spacing(0.5),
    },
    textCenter: {
        textAlign: 'center',
    },
    notices: {
        textAlign: 'center',
        whiteSpace: 'nowrap',
    },
    link: {
        color: theme.palette.info.main,
        textDecoration: 'none',
        '&:hover': {
            backgroundColor: theme.palette.hoverBG,
        },
    },
    tableActionIcon: {
        padding: theme.spacing(1),
    },
});

const skinnyTableHeader = {
    paddingRight: '0px',
    width: '1px',
};

const mediumTableHeader = {
    paddingRight: '0px',
    width: '10em',
};

const deviceStatus = (online, currentForegroundApp) => {
    if (!online || !currentForegroundApp) return '—';
    if (!currentForegroundApp.hmdOnUser) return 'Idle';
    return currentForegroundApp.title;
};

const compare = (a, b) => {
    if (a < b) {
        return -1;
    } else if (a > b) {
        return 1;
    } else {
        return 0;
    }
};

// constants used to help sort installed app filter
const sortLevels = { SUCCESS: 100, WARN: 50, ERROR: 20, NEUTRAL: 0 };

class DevicesTable extends Component {
    state = {
        filteredApp: {},
        filtersOpen: false,
        query: '',
        groupDevices: store.get('groupDevices_v2', true),
        filteredDeviceGroup: {},
        filteredOrganization: {},
        showFilters: store.get('showFilters', false),
    };

    getFilteredAppVersion = (device, defaultData = -1) => {
        try {
            return device.installedApps[this.state.filteredApp.packageName].versionCode; // will return undefined if app is not a managed app
        } catch (e) {
            return defaultData;
        }
    };

    deviceIsUpToDate = (device) => {
        let results = {};
        let upToDate = true;
        let deviceGroup = this.props.deviceGroups[device.deviceGroupRef.id];
        if (deviceGroup && deviceGroup.allApprovedAppIds) {
            deviceGroup.allApprovedAppIds.forEach((approvedAppId) => {
                let approvedApp = this.props.apps[approvedAppId];
                if (
                    !approvedApp ||
                    !device.installedApprovedAppVersions ||
                    !device.installedApprovedAppVersions[approvedApp.packageName]
                )
                    return; // TODO - caused by App Bundles. Should Deprecate App Bundles
                let [installedVersionCode, installedVersionName] = device.installedApprovedAppVersions[
                    approvedApp.packageName
                ].split(/\+(.+)/);
                let correctVersionInstalled =
                    installedVersionCode == approvedApp.latestVersion &&
                    (approvedApp.versionName === undefined || installedVersionName === approvedApp.versionName);
                if (!approvedApp.apkUrl && installedVersionCode > 0) {
                    // external apps are always valid
                    correctVersionInstalled = true;
                }
                upToDate = upToDate && correctVersionInstalled;
                results[approvedApp.packageName] = {
                    app: approvedApp,
                    targetVersion: `${approvedApp.latestVersion}+${approvedApp.versionName}`,
                    currentVersion: device.installedApprovedAppVersions[approvedApp.packageName],
                    upToDate: correctVersionInstalled,
                };
            });
        }

        if (deviceGroup && deviceGroup.targetDeviceSystemVersions && deviceGroup.targetDeviceSystemVersions[device.model]) {
            let targetDeviceSystemVersionRef = deviceGroup.targetDeviceSystemVersions[device.model];
            let targetDeviceSystemVersion = this.props.deviceSystemVersions[targetDeviceSystemVersionRef.id];
            if (targetDeviceSystemVersion && device.osDisplayVersion !== targetDeviceSystemVersion.version) {
                // console.log('OUT OF DATE', device, targetDeviceSystemVersion);
                results['PUI'] = {
                    app: { title: 'Device Firmware (Pico)' },
                    targetVersion: targetDeviceSystemVersion.version,
                    currentVersion: device.osDisplayVersion,
                    upToDate: false,
                };
                upToDate = false;
            }
        }

        let message = 'Device apps and firmware are up to date';
        if (!upToDate) {
            message = 'Outdated: ';
            _.mapValues(results, (value) => {
                if (!value.upToDate) {
                    message = message + `${value.app.title}, `;
                }
            });
            message = message.slice(0, -2); // remove last comma
        }

        return { results, upToDate, message };
    };

    renderDeviceIsUpToDate = (device) => {
        let { results, upToDate, message } = this.deviceIsUpToDate(device);
        if (upToDate) {
            return (
                <Tooltip title={message}>
                    <CheckCircleIcon className={this.props.classes.success} />
                </Tooltip>
            );
        } else {
            return (
                <Tooltip title={message}>
                    <WarningIcon className={this.props.classes.warning} />
                </Tooltip>
            );
        }
    };

    getInstalledAppInfoForDevice = (device) => {
        let result = {};
        let latestApprovedVersion = this.getLatestApprovedAppVersion(device, this.state.filteredApp);
        let installedVersion = this.getFilteredAppVersion(device);
        if (latestApprovedVersion < installedVersion && latestApprovedVersion !== -1) {
            result.className = this.props.classes.warning;
            result.text = `${installedVersion} (v${latestApprovedVersion})`;
            result.tooltip = 'Newer version is installed (This version was most likely manually sideloaded)';
            result.sortOrder = sortLevels.SUCCESS + 1 + (installedVersion - latestApprovedVersion);
        } else if (latestApprovedVersion === installedVersion && latestApprovedVersion !== -1) {
            result.className = this.props.classes.success;
            result.text = `${installedVersion} (v${latestApprovedVersion})`;
            result.tooltip = 'Latest version is installed';
            result.sortOrder = sortLevels.SUCCESS + 1;
        } else if (installedVersion < latestApprovedVersion && installedVersion !== -1) {
            result.className = this.props.classes.warning;
            result.text = `${installedVersion} (v${latestApprovedVersion})`;
            result.tooltip = 'Latest version is NOT installed';
            result.sortOrder = sortLevels.WARN + 1;
        } else if (installedVersion < latestApprovedVersion && latestApprovedVersion !== -1) {
            result.className = this.props.classes.error;
            result.text = `— (v${latestApprovedVersion})`;
            result.tooltip = 'Approved app is not installed';
            result.sortOrder = sortLevels.ERROR + 1;
        } else if (installedVersion === -1 && latestApprovedVersion === -1) {
            result.text = `—`;
            result.tooltip = 'App is not approved or installed';
            result.sortOrder = sortLevels.NEUTRAL;
        } else if (installedVersion === -1 && latestApprovedVersion === undefined) {
            result.className = this.props.classes.error;
            result.text = `— ( — )`;
            result.tooltip = 'Approved app is NOT installed (External App)';
            result.sortOrder = sortLevels.ERROR;
        } else if (installedVersion > -1 && latestApprovedVersion === undefined) {
            result.className = this.props.classes.success;
            result.text = `${installedVersion} ( — )`;
            result.tooltip = 'Approved app is installed (External App)';
            result.sortOrder = sortLevels.SUCCESS;
        } else {
            result.className = this.props.classes.warning;
            result.text = `${installedVersion} ( — )`;
            result.tooltip = 'App is installed, but not approved';
            result.sortOrder = sortLevels.WARN;
        }
        result.renderComponent = (
            <Tooltip title={result.tooltip}>
                <span className={result.className}>{result.text}</span>
            </Tooltip>
        );
        result.latestApprovedVersion = latestApprovedVersion;
        result.installedVersion = installedVersion;
        return result;
    };

    sortFilteredAppColumn = (deviceA, deviceB) => {
        let dataA = this.getInstalledAppInfoForDevice(deviceA);
        let dataB = this.getInstalledAppInfoForDevice(deviceB);
        if (dataA.sortOrder !== dataB.sortOrder) {
            return compare(dataA.sortOrder, dataB.sortOrder);
        }
        return compare(dataA.installedVersion, dataB.installedVersion);
    };

    renderFilteredAppColumn = (rowData) => {
        return this.getInstalledAppInfoForDevice(rowData).renderComponent;
    };

    getDeviceGroupName = (rowData, defaultData = '—') => {
        try {
            return this.props.deviceGroups[rowData.deviceGroupRef.id].name;
        } catch (e) {
            return defaultData;
        }
    };

    getLatestApprovedAppVersion = (device, app) => {
        try {
            let deviceGroup = this.props.deviceGroups[device.deviceGroupRef.id];
            if (deviceGroup.allApprovedAppIds.includes(app._id)) {
                return this.props.apps[app._id].latestVersion;
            }
        } catch (e) {}
        return -1;
    };

    getDeviceGroupRenderData = (deviceGroupId) => {
        let deviceGroup = this.props.deviceGroups[deviceGroupId] || {};
        let result = {};
        try {
            result.appCount = deviceGroup.allApprovedAppIds.length;
        } catch (e) {
            result.appCount = '—';
        }
        try {
            result.deviceCount = deviceGroup.allDeviceIds.length;
        } catch (e) {
            result.deviceCount = '—';
        }
        try {
            result.deviceIds = deviceGroup.allDeviceIds;
        } catch (e) {
            result.deviceIds = [];
        }
        try {
            result.name = deviceGroup.name;
        } catch (e) {
            result.name = '—';
        }
        try {
            result.registrationCode = deviceGroup.registrationCode;
        } catch (e) {
            result.registrationCode = '—';
        }

        return result;
    };

    renderDeviceGroupColumn = (rowData, rowType) => {
        if (rowType === 'group') {
            // We are grouping by deviceGroup. rowData = deviceGroupRef.id
            let deviceGroupId = rowData;
            let renderData = this.getDeviceGroupRenderData(deviceGroupId);
            return (
                <span>
                    <div style={{ display: 'inline-flex' }}>
                        <Link className={this.props.classes.link} to={createInfoUrl(DEVICE_GROUPS, deviceGroupId)}>
                            {renderData.name}
                        </Link>
                    </div>
                    <Grid container alignItems="center" style={{ marginTop: '-20px' }}>
                        <Grid
                            item
                            xs={8}
                            onClick={null}
                            className={this.props.classes.deviceGroupTitle}
                            style={{ padding: '6px 48px' }}
                        >
                            <Typography variant="caption">
                                {renderData.deviceCount} Devices | {renderData.appCount} Approved Apps
                            </Typography>
                            <br />
                            <Typography variant="caption" className={this.props.classes.hideUntilOpen}>
                                Registration Code: {renderData.registrationCode}
                            </Typography>
                        </Grid>
                        <Grid item xs={4}>
                            <Grid container justify="flex-end" style={{ marginTop: '-28px' }}>
                                <Grid item>
                                    <Tooltip title={'Device Group Info'} key={rowData}>
                                        <Link to={createInfoUrl(DEVICE_GROUPS, deviceGroupId)}>
                                            <IconButton
                                                aria-label="device-group-info"
                                                style={{
                                                    padding: '2px',
                                                    color: 'black',
                                                }}
                                            >
                                                <InfoIcon className={this.props.classes.infoIcon} />
                                            </IconButton>
                                        </Link>
                                    </Tooltip>
                                </Grid>
                                <Grid item>
                                    <IconButton
                                        aria-label="device-group-commands"
                                        style={{
                                            padding: '3px',
                                            marginRight: '6px',
                                        }}
                                        onClick={() =>
                                            this.props.onDeviceComandsClick({
                                                deviceKeys: renderData.deviceIds,
                                                deviceGroupKey: deviceGroupId,
                                                forGroup: true,
                                                title: renderData.name,
                                            })
                                        }
                                    >
                                        <CommandIcon />
                                    </IconButton>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </span>
            );
        }

        return (
            <Link className={this.props.classes.link} to={createInfoUrl(DEVICE_GROUPS, rowData.deviceGroupRef.id)}>
                {this.getDeviceGroupName(rowData)}
            </Link>
        );
    };

    handleAppFilterChange = (event) => {
        let filteredApp = this.props.apps[event.target.value] || {};
        let groupDevicesSetting = this.state.groupDevices;
        if (filteredApp.packageName) {
            this.props.getInstalledAppVersion(filteredApp.packageName);
            groupDevicesSetting = false; // by default, don't show groups when searching through filteredApps.
        }
        this.setState({
            filteredApp: filteredApp,
            groupDevices: groupDevicesSetting,
        });
    };

    handleDeviceGroupFilterChange = (event) => {
        let filteredDeviceGroup = this.props.deviceGroups[event.target.value] || {};
        this.setState({ filteredDeviceGroup: filteredDeviceGroup });
    };

    handleOrganizationFilterChange = (event) => {
        let filteredOrganization = this.props.organizations[event.target.value] || {};
        this.setState({ filteredOrganization: filteredOrganization });
    };

    renderNotificationsColumn = (rowData) => {
        return (
            <div className={this.props.classes.notices}>
                <ShowHide show={!!rowData.firstTimeUseModeEnabled}>
                    <Tooltip title={'First Time Use Mode'}>
                        <FirstIcon className={this.props.classes.success} />
                    </Tooltip>
                </ShowHide>
                <ShowHide show={!rowData.kioskModeEnabled}>
                    <Tooltip title={'Kiosk Mode Disabled'}>
                        <PauseIcon className={this.props.classes.warning} />
                    </Tooltip>
                </ShowHide>
            </div>
        );
    };

    getNoticesExportValue = (rowData) => {
        let result = '';
        if (rowData.firstTimeUseModeEnabled) {
            result += 'First Time Use Mode Enabled, ';
        }
        if (rowData.kioskModeEnabled) {
            result += 'Kiosk Mode Disabled, ';
        }
        result = result.slice(0, -2); // remove last comma
        return result;
    };

    getKeysFromFilteredAppData = (data) => {
        if (!data) return [];

        let keys = [];
        Object.keys(data).forEach((key) => {
            let version = parseInt(key);
            if (version > 0) {
                keys.push(`${key}`);
            }
        });
        keys.sort((a, b) => {
            compare(parseInt(a), parseInt(b));
        });
        return keys;
    };

    renderStatusColumn = (device) => {
        let { online, currentForegroundApp } = device;
        return (
            <Tooltip
                className={this.props.classes.textCenter}
                title={online ? `Online: ${deviceStatus(online, currentForegroundApp)}` : 'Offline'}
            >
                <div>
                    <OnlineIcon online={online} />
                </div>
            </Tooltip>
        );
    };

    onQueryChange = (value) => {
        this.setState({ query: value });
    };

    onChangeColumnHidden = (column, hidden) => {
        let columnSettings = store.get('columnSettings') || {};
        columnSettings[column.title] = hidden;
        store.set('columnSettings', columnSettings);
    };

    onChangeRowsPerPage = (numRows) => {
        let key = 'rowsPerPage';
        if (this.state.groupDevices) {
            key += '_Groups';
        } else {
            key += '_Devices';
        }
        store.set(key, numRows);
    };

    getStoredRowsPerPage = () => {
        let key = 'rowsPerPage';
        let defaultValue;
        if (this.state.groupDevices) {
            key += '_Groups';
            defaultValue = 5;
            if (this.props.deviceGroups.length >= 10) {
                defaultValue = 10;
            }
        } else {
            key += '_Devices';
            defaultValue = 25;
            if (this.props.devices.length >= 50) {
                defaultValue = 50;
            }
        }
        return store.get(key, defaultValue);
    };

    getStoredColumnHiddenSetting = (columnTitle, defaultVal = false) => {
        let columnSettings = store.get('columnSettings') || {};
        let result = columnSettings[columnTitle];
        if (result === undefined) {
            return defaultVal;
        } else {
            return result;
        }
    };

    toggleGroupDevicesSetting = () => {
        this.setGroupDeviceSetting(!this.state.groupDevices);
    };

    setGroupDeviceSetting = (newSetting) => {
        store.set('groupDevices_v2', newSetting);
        this.setState({ groupDevices: newSetting });
    };

    toggleShowFilters = () => {
        this.setShowFiltersSetting(!this.state.showFilters);
    };

    setShowFiltersSetting = (newSetting) => {
        store.set('showFilters', newSetting);
        this.setState({ showFilters: newSetting });
    };

    sortDeviceGroupColumn = (rowDataA, rowDataB, rowType) => {
        if (rowType === 'group') {
            let deviceGroupA = this.props.deviceGroups[rowDataA] || {};
            let deviceGroupB = this.props.deviceGroups[rowDataB] || {};
            return compare(deviceGroupA.name, deviceGroupB.name);
        }
        return compare(this.getDeviceGroupName(rowDataA), this.getDeviceGroupName(rowDataB));
    };

    getDeviceOrganizationName = (device) => {
        if (device.organizationRef) {
            let deviceOrg = this.props.organizations[device.organizationRef.id];
            if (deviceOrg) return deviceOrg.name;
        }
        return '';
    };

    getColumns = () => {
        let { filteredApp } = this.state;
        let { classes } = this.props;
        let deviceGroupCellStyle = {};
        if (this.state.groupDevices) {
            deviceGroupCellStyle = { backgroundColor: 'aliceblue' };
        }
        let columns = [
            {
                title: 'Status',
                field: 'online',
                exportValue: (device) => (device.online ? 'Online' : 'Offline'),
                render: this.renderStatusColumn,
                headerStyle: skinnyTableHeader,
                hidden: this.getStoredColumnHiddenSetting('Status', false),
            },
            {
                title: 'Current App',
                field: 'online',
                exportValue: (device) => {
                    if (device.online) return deviceStatus(device.online, device.currentForegroundApp);
                },
                render: (device) => deviceStatus(device.online, device.currentForegroundApp),
                hidden: this.getStoredColumnHiddenSetting('Current App', true),
            },
            {
                title: 'Name',
                field: 'name',
                render: (device) => (
                    <Link className={classes.link} to={createInfoUrl(DEVICES, device.serial)}>
                        {device.name}
                    </Link>
                ),
                hidden: this.getStoredColumnHiddenSetting('Name', false),
            },
            {
                title: 'Serial',
                field: 'serial',
                hidden: this.getStoredColumnHiddenSetting('Serial', false),
            },
            {
                title: 'Device Group',
                field: 'deviceGroupRef.id',
                exportValue: this.getDeviceGroupName,
                defaultGroupOrder: this.state.groupDevices ? 0 : undefined,
                render: this.renderDeviceGroupColumn,
                hidden: this.getStoredColumnHiddenSetting('Device Group', false),
                customSort: this.sortDeviceGroupColumn,
                customFilterAndSearch: (term, rowData) =>
                    this.getDeviceGroupName(rowData)
                        .toLowerCase()
                        .indexOf(term.toLowerCase()) >= 0,
                cellStyle: deviceGroupCellStyle,
            },
            {
                title: 'Last Check-In',
                field: 'lastUpdated.seconds',
                exportValue: (device) => timeStampToLongString(device.lastUpdated),
                render: (device) => timeStampToLongString(device.lastUpdated),
                customSort: (deviceA, deviceB) =>
                    compare(_.get(deviceB, 'lastUpdated.seconds') || 0, _.get(deviceA, 'lastUpdated.seconds') || 0),
                hidden: this.getStoredColumnHiddenSetting('Last Check-In', false),
                emptyValue: '—',
            },
            {
                title: 'Battery',
                field: 'batteryLevel',
                hidden: this.getStoredColumnHiddenSetting('Battery', true),
                emptyValue: '—',
            },
            {
                title: 'RAM - Current Usage',
                field: 'ram.availableRam',
                exportValue: (device) => {
                    let ram = device.ram || {};
                    return bytesToGb(ram.totalRam - ram.availableRam);
                },
                render: (device) => bytesToGb(device.ram.totalRam - device.ram.availableRam),
                hidden: this.getStoredColumnHiddenSetting('RAM - Current Usage', true),
                emptyValue: '—',
            },
            {
                title: 'RAM - Total',
                field: 'ram.totalRam',
                exportValue: (device) => {
                    let ram = device.ram || {};
                    return bytesToGb(ram.totalRam);
                },
                render: (device) => bytesToGb(device.ram.totalRam),
                hidden: this.getStoredColumnHiddenSetting('RAM - Total', true),
                emptyValue: '—',
            },
            {
                title: 'Available Storage',
                field: 'availableStorage',
                exportValue: (device) => bytesToGb(device.availableStorage),
                render: (device) => bytesToGb(device.availableStorage),
                hidden: this.getStoredColumnHiddenSetting('Available Storage', true),
                emptyValue: '—',
            },
            {
                title: 'Manufacturer',
                field: 'manufacturer',
                hidden: this.getStoredColumnHiddenSetting('Manufacturer', true),
                emptyValue: '—',
            },
            {
                title: 'Model',
                field: 'model',
                hidden: this.getStoredColumnHiddenSetting('Model', true),
                emptyValue: '—',
            },
            {
                title: 'OS Version',
                field: 'osVersion',
                hidden: this.getStoredColumnHiddenSetting('OS Version', true),
                emptyValue: '—',
            },
            {
                title: 'PUI Version',
                field: 'osDisplayVersion',
                hidden: this.getStoredColumnHiddenSetting('PUI Version', true),
                exportValue: (device) => getDevicePUIVersion(device),
                render: (device) => getDevicePUIVersion(device) || '—',
                customSort: (deviceA, deviceB) =>
                    compareVersions(deviceB.osDisplayVersion || '0', deviceA.osDisplayVersion || '0'),
                emptyValue: '—',
            },
            {
                title: 'Pico Controller Versions',
                field: 'picoCvControllerVersions',
                hidden: this.getStoredColumnHiddenSetting('Pico Controller Version', true),
                exportValue: (device) => getDevicePicoControllerVersion(device),
                render: (device) => getDevicePicoControllerVersion(device) || '—',
                customSort: (deviceA, deviceB) =>
                    compare(getDevicePicoControllerVersion(deviceA), getDevicePicoControllerVersion(deviceB)),
                emptyValue: '—',
            },
            {
                title: 'MAC',
                field: 'mac',
                hidden: this.getStoredColumnHiddenSetting('MAC', true),
                emptyValue: '—',
            },
            {
                title: 'Location - City',
                field: 'location.city',
                hidden: this.getStoredColumnHiddenSetting('Location - City', true),
                emptyValue: '—',
            },
            {
                title: 'Location - Region',
                field: 'location.region',
                hidden: this.getStoredColumnHiddenSetting('Location - Region', true),
                emptyValue: '—',
            },
            {
                title: 'Location - Country',
                field: 'location.country',
                hidden: this.getStoredColumnHiddenSetting('Location - Country', true),
                emptyValue: '—',
            },
            {
                title: 'Apps Up To Date',
                field: 'installedApprovedAppVersions',
                hidden: this.getStoredColumnHiddenSetting('Apps Up To Date', false),
                exportValue: (device) => this.deviceIsUpToDate(device).message,
                render: (device) => this.renderDeviceIsUpToDate(device),
                customSort: (deviceA, deviceB) =>
                    compare(this.deviceIsUpToDate(deviceA).upToDate, this.deviceIsUpToDate(deviceB).upToDate),
                headerStyle: mediumTableHeader,
                emptyValue: '—',
            },
            {
                title: 'Kiosk Mode',
                field: 'kioskModeEnabled',
                hidden: this.getStoredColumnHiddenSetting('Kiosk Mode', true),
                exportValue: (device) => (device.kioskModeEnabled ? 'True' : 'False'),
                render: (device) => {
                    if (device.kioskModeEnabled === undefined || device.kioskModeEnabled) {
                        return <CheckCircleIcon className={this.props.classes.success} />;
                    } else {
                        return <PauseIcon className={this.props.classes.warning} />;
                    }
                },
                // customSort: (deviceA, deviceB) => compare(this.deviceIsUpToDate(deviceA).upToDate, this.deviceIsUpToDate(deviceB).upToDate),
                headerStyle: mediumTableHeader,
                emptyValue: '—',
            },
            {
                title: 'First Time Use Mode',
                field: 'firstTimeUseModeEnabled',
                hidden: this.getStoredColumnHiddenSetting('First Time Use Mode', true),
                exportValue: (device) => (device.firstTimeUseModeEnabled ? 'True' : 'False'),
                render: (device) => (device.firstTimeUseModeEnabled ? <FirstIcon className={this.props.classes.success} /> : '—'),
                // customSort: (deviceA, deviceB) => compare(this.deviceIsUpToDate(deviceA).upToDate, this.deviceIsUpToDate(deviceB).upToDate),
                headerStyle: mediumTableHeader,
                emptyValue: '—',
            },
        ];

        if (filteredApp.packageName) {
            columns.push({
                title: `${filteredApp.title} Version`,
                render: (rowData) => this.getInstalledAppInfoForDevice(rowData).renderComponent,
                customSort: this.sortFilteredAppColumn,
                customFilterAndSearch: (term, rowData) => parseInt(term) === this.getFilteredAppVersion(rowData),
                defaultSort: 'desc',
                hidden: false,
            });
        }

        if (isCurrentUserSuperAdmin()) {
            columns.push({
                title: `Organization`,
                field: 'organizationRef.id',
                exportValue: this.getDeviceOrganizationName,
                render: (rowData) => this.getDeviceOrganizationName(rowData),
                customSort: (deviceA, deviceB) =>
                    compare(this.getDeviceOrganizationName(deviceA), this.getDeviceOrganizationName(deviceB)),
                customFilterAndSearch: (term, rowData) =>
                    this.getDeviceOrganizationName(rowData)
                        .toLowerCase()
                        .indexOf(term.toLowerCase()) >= 0,
                hidden: this.getStoredColumnHiddenSetting('Organization', true),
            });
        }

        columns.push({
            title: 'Notices',
            field: 'firstTimeUseModeEnabled',
            exportValue: this.getNoticesExportValue,
            customSort: (deviceA, deviceB) => compare(deviceA.kioskModeEnabled, deviceB.kioskModeEnabled),
            render: this.renderNotificationsColumn,
            headerStyle: skinnyTableHeader,
            hidden: this.getStoredColumnHiddenSetting('Notices', false),
        });

        return columns;
    };

    // We're grouping by DeviceGroup in the table, but by default the table won't show a device group
    // if it doesn't have any devices. So we have to create placeholder devices for groups with 0 devices
    getTableData = () => {
        let data = Object.values(this.props.devices);
        if (this.state.groupDevices) {
            Object.values(this.props.deviceGroups).forEach((deviceGroup) => {
                try {
                    if (!deviceGroup.allDeviceIds || deviceGroup.allDeviceIds.length === 0) {
                        data.push({
                            deviceGroupRef: { id: deviceGroup._id },
                            _tablePlaceholder: true,
                        });
                    }
                } catch {}
            });
        }

        if (this.state.filteredDeviceGroup._id) {
            let filteredData = [];
            data.forEach((device) => {
                if (device.deviceGroupRef.id === this.state.filteredDeviceGroup._id) {
                    filteredData.push(device);
                }
            });
            data = filteredData;
        }

        if (this.state.filteredOrganization._id) {
            let filteredData = [];
            data.forEach((device) => {
                if (device.organizationRef && device.organizationRef.id === this.state.filteredOrganization._id) {
                    filteredData.push(device);
                }
            });
            data = filteredData;
        }

        return data;
    };

    // Hide row if it's for a deviceGroup placeholder
    getRowStyle = (rowData) => {
        if (rowData._tablePlaceholder) {
            return { display: 'none' };
        }
        return {};
    };

    render() {
        // console.log("RENDER (Device Table)", this.getTableData());

        // if (store.get('groupDevices_v2', "unset") === "unset" && Object.keys(this.props.devices).length > 250 && this.state.groupDevices) {
        //     // quick performance improver, show table without groups if > 100 devices (and hasn't set the setting yet)
        //     this.setState({ groupDevices: false });
        // }

        let {
            classes,
            apps,
            deviceGroups,
            organizations,
            onAddNewDeviceGroupClick,
            onAddNewDeviceClick,
            onDeviceComandsClick,
            history,
        } = this.props;

        let pageSize, pageSizeOptions, groupActionIcon;
        if (this.state.groupDevices) {
            pageSize = this.getStoredRowsPerPage();
            pageSizeOptions = [1, 5, 10, 25];
            groupActionIcon = <CheckBoxIcon />;
        } else {
            pageSize = this.getStoredRowsPerPage();
            pageSizeOptions = [10, 25, 50, 100, 250];
            groupActionIcon = <IndeterminateCheckBoxIcon />;
        }

        return (
            <div>
                <Grid container justify="space-between">
                    <Grid item xs={12}>
                        <Typography variant="h5" className={classes.pageTitle}>
                            Devices
                            <AddNewButton onClick={onAddNewDeviceGroupClick} title={'New Device Group'} />
                            <AddNewButton onClick={onAddNewDeviceClick} title={'New Device'} />
                        </Typography>
                    </Grid>
                </Grid>

                <Paper>
                    <Grid container justify="flex-end">
                        <Grid item>
                            <Collapse in={this.state.showFilters}>
                                <FormControl className={classes.formControl}>
                                    <InputLabel htmlFor="filtered-app-select">Installed App Filter</InputLabel>
                                    <Select
                                        native
                                        value={this.state.filteredApp._id || ''}
                                        onChange={this.handleAppFilterChange}
                                        inputProps={{
                                            name: 'filtered-app',
                                            id: 'filtered-app-select',
                                        }}
                                    >
                                        <option aria-label="None" value="" />
                                        {_.map(apps, (app) => (
                                            <option key={app._id} value={app._id}>
                                                {app.title}
                                            </option>
                                        ))}
                                    </Select>
                                </FormControl>
                                <FormControl className={classes.formControl}>
                                    <InputLabel htmlFor="filtered-device-group-select">Device Group</InputLabel>
                                    <Select
                                        native
                                        value={this.state.filteredDeviceGroup._id || ''}
                                        onChange={this.handleDeviceGroupFilterChange}
                                        inputProps={{
                                            name: 'filtered-device-group',
                                            id: 'filtered-device-group-select',
                                        }}
                                    >
                                        <option aria-label="None" value="" />
                                        {_.map(deviceGroups, (deviceGroup) => (
                                            <option key={deviceGroup._id} value={deviceGroup._id}>
                                                {deviceGroup.name}
                                            </option>
                                        ))}
                                    </Select>
                                </FormControl>
                                {isCurrentUserSuperAdmin() ? (
                                    <FormControl className={classes.formControl}>
                                        <InputLabel htmlFor="filtered-organization-select">Organization</InputLabel>
                                        <Select
                                            native
                                            value={this.state.filteredOrganization._id || ''}
                                            onChange={this.handleOrganizationFilterChange}
                                            inputProps={{
                                                name: 'filtered-organization',
                                                id: 'filtered-organization-select',
                                            }}
                                        >
                                            <option aria-label="None" value="" />
                                            {_.map(organizations, (org) => (
                                                <option key={org._id} value={org._id}>
                                                    {org.name}
                                                </option>
                                            ))}
                                        </Select>
                                    </FormControl>
                                ) : (
                                    ''
                                )}
                            </Collapse>
                        </Grid>
                    </Grid>

                    <MaterialTable
                        key={this.state.filteredApp._id || 'default'}
                        title="Devices"
                        columns={this.getColumns()}
                        data={this.getTableData()}
                        actions={[
                            {
                                icon: () => groupActionIcon,
                                tooltip: 'Group Devices',
                                onClick: this.toggleGroupDevicesSetting,
                                isFreeAction: true,
                            },
                            {
                                icon: () => <FilterIcon />,
                                tooltip: this.state.showFilters ? 'Hide Filters' : 'Show Filters',
                                onClick: this.toggleShowFilters,
                                isFreeAction: true,
                            },
                            {
                                icon: () => <InfoIcon className={classes.infoIcon} />,
                                tooltip: 'Device Info',
                                onClick: (event, device) => history.push(createInfoUrl(DEVICES, device.serial)),
                            },
                            {
                                icon: () => <CommandIcon title="" />,
                                tooltip: 'Commands',
                                onClick: (event, device) =>
                                    onDeviceComandsClick({
                                        deviceKeys: [device.serial],
                                        deviceGroupKey: device.deviceGroupRef.id,
                                        forGroup: false,
                                        title: device.name,
                                    }),
                            },
                        ]}
                        icons={{
                            SortArrow: ArrowUpwardIcon,
                        }}
                        onChangeColumnHidden={this.onChangeColumnHidden}
                        onChangeRowsPerPage={this.onChangeRowsPerPage}
                        options={{
                            sorting: true,
                            exportButton: true,
                            exportAllData: true,
                            pageSize: pageSize,
                            pageSizeOptions: pageSizeOptions,
                            padding: 'dense',
                            actionsColumnIndex: -1,
                            columnsButton: true,
                            showTitle: false,
                            toolbarButtonAlignment: 'right',
                            draggable: false,
                            defaultExpanded: Object.keys(this.props.devices).length < 50,
                            emptyRowsWhenPaging: false,
                            rowStyle: this.getRowStyle,
                            debounceInterval: 0,
                        }}
                        components={{
                            Container: Box,
                        }}
                    />
                </Paper>
            </div>
        );
    }
}

const mapStateToProps = (state) => ({
    devices: state.data.devices,
    deviceGroups: state.data.deviceGroups,
    contentBundles: state.data.contentBundles,
    apps: state.data.apps,
    deviceSystemVersions: state.data.deviceSystemVersions,
    organizations: state.data.organizations,
});

const mapDispatchToProps = { getInstalledAppVersion };

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(withStyles(styles)(withRouter(DevicesTable)));
